import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import { supabase } from "@/integrations/supabase/client";
import { Key, Mail, ArrowLeft, Check, Eye, EyeOff } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { validatePassword } from "@/lib/security";

type ResetStep = "request" | "verify" | "new-password" | "success";

interface PasswordResetProps {
  userEmail: string;
  onClose: () => void;
}

export function PasswordReset({ userEmail, onClose }: PasswordResetProps) {
  const [step, setStep] = useState<ResetStep>("request");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { toast } = useToast();
  const { isLoading, countdown, requestVerification, verifyCode } = useEmailVerification();

  const handleSendCode = async () => {
    const result = await requestVerification({ 
      email: userEmail, 
      type: "password_reset" 
    });
    
    if (result.success) {
      setStep("verify");
      toast({
        title: "验证码已发送",
        description: "请查收邮件并输入验证码",
      });
    } else {
      toast({
        title: "发送失败",
        description: result.error || "请稍后再试",
        variant: "destructive",
      });
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast({
        title: "请输入完整验证码",
        description: "验证码为6位数字",
        variant: "destructive",
      });
      return;
    }

    const result = await verifyCode(userEmail, verificationCode, "password_reset");
    if (result.success && result.verified) {
      setStep("new-password");
    } else {
      toast({
        title: "验证失败",
        description: result.error || "验证码无效或已过期",
        variant: "destructive",
      });
    }
  };

  const handleResetPassword = async () => {
    const pwdValidation = validatePassword(newPassword);
    if (!pwdValidation.valid) {
      toast({
        title: "密码不符合要求",
        description: pwdValidation.message,
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "密码不一致",
        description: "两次输入的密码不一致",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setStep("success");
      toast({
        title: "密码重置成功",
        description: "您的密码已更新",
      });
    } catch (error: any) {
      toast({
        title: "重置失败",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          {step !== "request" && step !== "success" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (step === "verify") setStep("request");
                else if (step === "new-password") setStep("verify");
              }}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            修改密码
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {step === "request" && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">当前邮箱</p>
                  <p className="text-sm text-muted-foreground">{userEmail}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              我们将向您的邮箱发送验证码以验证身份
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleSendCode}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? "发送中..." : "发送验证码"}
              </Button>
              <Button variant="outline" onClick={onClose}>
                取消
              </Button>
            </div>
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground text-center">
              验证码已发送至 <span className="text-foreground font-medium">{userEmail}</span>
            </p>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={setVerificationCode}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              onClick={handleVerifyCode}
              className="w-full"
              disabled={isLoading || verificationCode.length !== 6}
            >
              验证
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleSendCode}
                disabled={countdown > 0 || isLoading}
                className="text-sm text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
              >
                {countdown > 0 ? `重新发送 (${countdown}s)` : "重新发送验证码"}
              </button>
            </div>
          </div>
        )}

        {step === "new-password" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">新密码</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="请输入新密码"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">至少8位，包含字母和数字</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">确认密码</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="请再次输入新密码"
              />
            </div>
            <Button
              onClick={handleResetPassword}
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "处理中..." : "确认修改"}
            </Button>
          </div>
        )}

        {step === "success" && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">密码修改成功</h3>
              <p className="text-sm text-muted-foreground mt-1">
                下次登录时请使用新密码
              </p>
            </div>
            <Button onClick={onClose} className="w-full">
              完成
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
